<template>
  <div class="wallet_detail" v-loading="loading">
    <div class="title-row">
      <button type="button" class="back-btn" @click="goBack" aria-label="返回">
        <img src="@/assets/images/svg/back.svg" alt="" />
      </button>
      <div class="title">账单详情</div>
    </div>
    <div class="content" v-if="flow">
      <div class="top">
        <div class="user">
          <img class="avatar" :src="avatarSrc" alt="" />
        </div>
        <div class="top_top">
          <div class="name">{{ flowTitle }}</div>
          <div
            class="num"
            :class="{
              'num--plus': flow.changeAmount > 0,
              'num--minus': flow.changeAmount < 0,
            }"
          >
            {{ formatSignedAmount(flow.changeAmount) }}
          </div>
          <div class="type">{{ flow.currencyCode || "GOLD" }} 账户变动</div>
        </div>
        <div class="top_down">
          <div class="down_content">
            <div class="left">当前状态</div>
            <div class="right">已完成</div>
          </div>
          <div class="down_content">
            <div class="left">变动时间</div>
            <div class="right">{{ formatDetailTime(flow.createdAt) }}</div>
          </div>
          <div class="down_content">
            <div class="left">关联游戏</div>
            <div class="right">游戏 #{{ flow.gameId || "—" }}</div>
          </div>
          <div class="down_content">
            <div class="left">业务场景</div>
            <div class="right">{{ sceneText }}</div>
          </div>
          <div class="down_content">
            <div class="left">变动后余额</div>
            <div class="right">{{ formatAmount(flow.balanceAfter) }}</div>
          </div>
          <div class="down_content">
            <div class="left">流水单号</div>
            <div class="right">#{{ flow.id }}</div>
          </div>
        </div>
      </div>
      <div class="down">
        <div class="item" @click="goBack">返回全部账单</div>
        <div class="item" @click="goWallet">我的钱包</div>
      </div>
    </div>
    <div v-else-if="!loading" class="empty">未找到账单记录</div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useUserStore } from "@/stores/user";
import { getWalletFlows } from "@/api/gameCore";
import defaultAvatar from "@/assets/images/avatar.jpg";

const FLOW_DETAIL_KEY = "wallet_flow_detail";

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const loading = ref(false);
const flow = ref(null);

const avatarSrc = computed(() => userStore.userInfo?.icon || defaultAvatar);

const sceneLabelMap = {
  MATCH_REWARD: "对局奖励",
  MATCH_COST: "对局消耗",
  RECHARGE: "虚拟货币充值",
  CONSUME: "消费支出",
};

const sceneText = computed(() => {
  if (!flow.value) return "—";
  if (flow.value.remark) return flow.value.remark;
  if (flow.value.scene && sceneLabelMap[flow.value.scene]) {
    return sceneLabelMap[flow.value.scene];
  }
  return flow.value.scene || "—";
});

const flowTitle = computed(() => {
  if (!flow.value) return "—";
  if (flow.value.remark) return flow.value.remark;
  if (flow.value.scene && sceneLabelMap[flow.value.scene]) {
    return sceneLabelMap[flow.value.scene];
  }
  return flow.value.scene || "账户变动";
});

const formatAmount = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString("zh-CN");
};

const formatSignedAmount = (value) => {
  const num = Number(value) || 0;
  const prefix = num > 0 ? "+" : "";
  return `${prefix}${num.toLocaleString("zh-CN")}`;
};

const formatDetailTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hour = String(d.getHours()).padStart(2, "0");
  const minute = String(d.getMinutes()).padStart(2, "0");
  const second = String(d.getSeconds()).padStart(2, "0");
  return `${y}年${m}月${day}日 ${hour}:${minute}:${second}`;
};

const loadFlow = async () => {
  loading.value = true;
  try {
    const cached = sessionStorage.getItem(FLOW_DETAIL_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (!route.query.id || String(parsed.id) === String(route.query.id)) {
        flow.value = parsed;
        return;
      }
    }
    const res = await getWalletFlows();
    if (res?.code === 200 && Array.isArray(res.data)) {
      const found = res.data.find(
        (item) => String(item.id) === String(route.query.id)
      );
      flow.value = found || null;
    }
  } catch (error) {
    console.error("加载账单详情失败:", error);
    flow.value = null;
  } finally {
    loading.value = false;
  }
};

const goBack = () => {
  router.push("/wallet");
};

const goWallet = () => {
  router.push("/wallet");
};

onMounted(() => {
  loadFlow();
});
</script>

<style lang="scss" scoped>
.wallet_detail {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 86px 240px 96px;
  background: transparent;
  min-height: 100vh;

  .title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding-top: 36px;
  }

  .back-btn {
    width: 40px;
    height: 40px;
    border: none;
    border-radius: var(--ios-radius-md, 14px);
    background: var(--ios-fill-tertiary);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    img {
      width: 20px;
      height: 20px;
      transform: rotate(180deg);
    }
  }

  .title {
    color: var(--ux-text-primary);
    font-size: 24px;
    font-weight: 600;
  }

  .empty {
    padding: 120px 0;
    text-align: center;
    color: var(--ux-text-secondary, #9fb4d6);
    font-size: 16px;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 0;
    padding-top: 36px;
    padding-bottom: 40px;
    width: 100%;
    max-width: 900px;
    margin-left: auto;
    margin-right: auto;

    .top {
      background-color: var(--ux-glass-bg);
      width: min(766px, 100%);
      min-height: 620px;
      border-radius: 20px;
      border: 0.5px dashed var(--ux-glass-border);
      position: relative;
      backdrop-filter: var(--ios-glass-blur-light);
      -webkit-backdrop-filter: var(--ios-glass-blur-light);
      box-shadow: var(--ux-glass-shadow);

      .user {
        position: absolute;
        left: 50%;
        top: -41px;
        transform: translateX(-50%);
        width: 82px;
        height: 82px;
        border-radius: 50%;
        overflow: hidden;
        border: 0.5px solid var(--ux-glass-border);
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
      }
      .top_top {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: 91px;
        .name {
          font-size: 18px;
          font-weight: 600;
          color: var(--ux-text-primary);
        }
        .num {
          font-size: 28px;
          font-weight: 700;
          color: var(--ux-text-primary);
          margin: 20px 0;
        }
        .num--plus {
          color: #4ade80;
        }
        .num--minus {
          color: #f87171;
        }
        .type {
          font-size: 16px;
          color: var(--ux-text-secondary, #9fb4d6);
        }
      }
      .top_down {
        display: flex;
        flex-direction: column;
        margin-top: 72px;
        margin-left: 90px;
        margin-right: 40px;
        padding-bottom: 40px;
        .down_content {
          display: flex;
          color: var(--ux-text-primary);
          margin-bottom: 24px;
          .left {
            font-size: 16px;
            margin-right: 48px;
            width: 7em;
            flex-shrink: 0;
            color: var(--ux-text-secondary, #9fb4d6);
          }
          .right {
            font-size: 16px;
            font-weight: 500;
            word-break: break-all;
          }
        }
      }
    }
    .down {
      background-color: var(--ux-glass-bg);
      width: min(766px, 100%);
      min-height: 120px;
      margin-top: 18px;
      border-radius: 20px;
      display: flex;
      flex-wrap: wrap;
      padding: 32px 40px;
      gap: 16px;
      border: 0.5px solid var(--ux-glass-border);
      .item {
        min-width: 40%;
        font-size: 16px;
        color: var(--ux-accent, #00d4ff);
        cursor: pointer;
        &:hover {
          opacity: 0.85;
        }
      }
    }
  }
}

@media (max-width: 1200px) {
  .wallet_detail {
    padding-left: 48px;
    padding-right: 48px;
  }
}

@media (max-width: 768px) {
  .wallet_detail {
    padding: 86px 20px 64px;
  }
}
</style>
